import React, { useState, useEffect } from 'react';
import { 
    DataGrid, 
    GridColDef, 
    GridRowsProp, 
    GridRowModel, 
    useGridApiRef, 
    GridEventListener,
    GridColumnResizeParams,
    GridCellParams
} from '@mui/x-data-grid';
import { 
    Button, 
    Box, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField,
    Tooltip,
    IconButton
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

export interface KeyValuePair {
    [key: string]: any;
}

interface EditableTableProps {
    data: KeyValuePair[];
    onChange?: (newData: KeyValuePair[]) => void;
    columns?: GridColDef[];
    readOnly?: boolean;
    rowStyle?: (row: KeyValuePair) => React.CSSProperties;
    documentType?: string;
    title?: string; // Optional title for the CSV download filename
}

export const EditableTable: React.FC<EditableTableProps> = ({ 
    data, 
    onChange, 
    columns, 
    readOnly = false, 
    rowStyle,
    documentType,
    title = 'table-data'
}) => {
    const [currentData, setCurrentData] = useState<KeyValuePair[]>([]);
    const [columnWidths, setColumnWidths] = useState<{[key: string]: number}>({});
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [newColumnDialog, setNewColumnDialog] = useState(false);
    const [newColumnName, setNewColumnName] = useState('');
    const apiRef = useGridApiRef();

    useEffect(() => {
        setCurrentData(data);
    }, [data]);

    const hasSourceFields = currentData.some(row => 
        'source_text' in row || 'location' in row || 'source_url' in row
    );

    // Apply stored column widths to default columns
    const getDefaultColumns = (): GridColDef[] => {
        const cols: GridColDef[] = [
            { 
                field: 'Key', 
                headerName: 'Key', 
                width: columnWidths['Key'] || 200, 
                editable: !readOnly, 
                renderCell: (params) => {
                    const isKeyMismatched = params.row.isKeyMismatched;
                    return (
                        <span 
                            style={{
                                whiteSpace: 'pre-wrap',
                                color: isKeyMismatched ? '#d32f2f' : 'inherit',
                                fontWeight: isKeyMismatched ? 'bold' : 'normal',
                                padding: isKeyMismatched ? '2px 6px' : '0',
                                borderRadius: isKeyMismatched ? '4px' : '0',
                                backgroundColor: isKeyMismatched ? '#ffcdd2' : 'transparent',
                                display: 'inline-block'
                            }}
                        >
                            {prettyValue(params.value)}
                        </span>
                    );
                }
            },
            { field: 'Value', headerName: 'Value', width: columnWidths['Value'] || 400, editable: !readOnly, renderCell: (params) => <span style={{whiteSpace:'pre-wrap'}}>{prettyValue(params.value)}</span> },
        ];

        if (hasSourceFields) {
            cols.push(
                { field: 'source_text', headerName: 'Source Text', width: columnWidths['source_text'] || 300, editable: !readOnly },
                { field: 'location', headerName: 'Location', width: columnWidths['location'] || 150, editable: !readOnly }
            );
            if (documentType !== 'credit_card' && documentType !== 'pdf') {
                cols.push(
                    { field: 'source_url', headerName: 'Source URL', width: columnWidths['source_url'] || 200, editable: !readOnly }
                );
            }
        }
        return cols;
    };

    // Apply stored column widths to custom columns
    const getCustomColumns = (): GridColDef[] => {
        if (!columns) return [];
        
        return columns.map(col => ({
            ...col,
            width: columnWidths[col.field] || col.width
        }));
    };

    const defaultColumns = getDefaultColumns();

    // Helper to pretty-print objects/arrays in cells
    const prettyValue = (val: any) => {
        if (typeof val === 'object' && val !== null) {
            return JSON.stringify(val, null, 1);
        }
        return val;
    };

    const rows: GridRowsProp = currentData.map((row, index) => {
        // For each cell, pretty print if object/array
        const prettyRow: Record<string, any> = { id: index };
        Object.entries(row).forEach(([k, v]) => {
            prettyRow[k] = prettyValue(v);
        });
        return prettyRow;
    });

    const handleProcessRowUpdate = (newRow: GridRowModel, oldRow: GridRowModel) => {
        console.log('Updated row:', newRow);
        
        const updatedData = [...currentData];
        const rowIndex = newRow.id as number;
        
        // Create a clean version of the updated row
        const updatedRow = { ...newRow };
        delete updatedRow.id;
        
        // Track if key has changed to notify parent component
        if (updatedRow.Key !== oldRow.Key) {
            updatedRow._originalKey = oldRow.Key;
        }
        
        // Parse any JSON strings back to objects
        Object.entries(updatedRow).forEach(([key, value]) => {
            if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
                try {
                    const parsed = JSON.parse(value);
                    updatedRow[key] = parsed;
                } catch {
                    // If not valid JSON, keep as string
                }
            }
        });
        
        updatedData[rowIndex] = updatedRow;
        setCurrentData(updatedData);
        onChange?.(updatedData);
        
        return newRow;
    };

    const handleCellEditStop: GridEventListener<'cellEditStop'> = (params, event) => {
        // Optional: Just ensure edit mode is actually ended (without overriding the natural commit)
        apiRef.current.stopCellEditMode({
          id: params.id,
          field: params.field,
          ignoreModifications: false,
        });
    };

    // Handle column width changes
    const handleColumnResize: GridEventListener<'columnResize'> = (params: GridColumnResizeParams) => {
        setColumnWidths(prev => ({
            ...prev,
            [params.colDef.field]: params.width
        }));
    };

    // Function to convert data to CSV format and download it
    const downloadCSV = () => {
        // Determine which columns to use
        const tableColumns = columns || defaultColumns;
        
        // Create CSV header row
        const headers = tableColumns.map(col => `"${col.headerName || col.field}"`).join(',');
        
        // Create CSV data rows
        const csvRows = currentData.map(row => {
            return tableColumns.map(col => {
                // Get the value for this column
                let value = row[col.field];
                
                // Handle objects and arrays by converting to JSON string
                if (typeof value === 'object' && value !== null) {
                    value = JSON.stringify(value);
                }
                
                // Escape quotes and wrap in quotes
                if (value === null || value === undefined) {
                    return '""';
                }
                return `"${String(value).replace(/"/g, '""')}"`;
            }).join(',');
        }).join('\n');
        
        // Combine header and rows
        const csvContent = `${headers}\n${csvRows}`;
        
        // Create a blob and download link
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Add row functionality
    const handleAddRow = () => {
        // Create empty row with all columns
        const colFields = (columns || defaultColumns).map(col => col.field);
        const newRow: KeyValuePair = {};
        
        // If using default key/value columns, set default values for them
        if (!columns) {
            newRow['Key'] = 'new_key';
            newRow['Value'] = '';
            
            // Add source fields if they exist in other rows
            if (hasSourceFields) {
                newRow['source_text'] = '';
                newRow['location'] = '';
                if (documentType !== 'credit_card' && documentType !== 'pdf') {
                    newRow['source_url'] = '';
                }
            }
        } else {
            // For custom columns, initialize all fields as empty
            colFields.forEach(field => {
                newRow[field] = '';
            });
        }
        
        const updatedData = [...currentData, newRow];
        setCurrentData(updatedData);
        onChange?.(updatedData);
    };

    // Delete selected rows
    const handleDeleteRows = () => {
        if (selectedRows.length === 0) return;
        
        const updatedData = currentData.filter((_, index) => 
            !selectedRows.includes(index)
        );
        
        setCurrentData(updatedData);
        onChange?.(updatedData);
        setSelectedRows([]);
    };

    // Add column functionality
    const handleAddColumn = () => {
        if (!newColumnName.trim()) return;
        
        // Create a new data array with the new column
        const updatedData = currentData.map(row => ({
            ...row,
            [newColumnName]: ''
        }));
        
        setCurrentData(updatedData);
        onChange?.(updatedData);
        setNewColumnDialog(false);
        setNewColumnName('');
    };

    // Delete column functionality
    const handleDeleteColumn = (columnName: string) => {
        // Get current columns
        const currentColumns = columns || defaultColumns;
        if (currentColumns.length <= 1) return; // Prevent deleting the last column
        
        // Create a new data array without the deleted column
        const updatedData = currentData.map(row => {
            const newRow = {...row};
            delete newRow[columnName];
            return newRow;
        });
        
        setCurrentData(updatedData);
        onChange?.(updatedData);
    };

    return (
        <Box sx={{ width: '100%', height: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Tooltip title="Download as CSV">
                    <IconButton 
                        onClick={downloadCSV}
                        size="small"
                        sx={{ mr: 1 }}
                        color="primary"
                    >
                        <DownloadIcon />
                    </IconButton>
                </Tooltip>
                
                {!readOnly && (
                    <>
                        <Button 
                            startIcon={<AddIcon />} 
                            onClick={handleAddRow}
                            variant="outlined"
                            size="small"
                            sx={{ mr: 1 }}
                        >
                            Add Row
                        </Button>
                        <Button 
                            startIcon={<DeleteIcon />} 
                            onClick={handleDeleteRows}
                            variant="outlined"
                            size="small"
                            disabled={selectedRows.length === 0}
                            color="error"
                        >
                            Delete Selected
                        </Button>
                    </>
                )}
            </Box>
            <div style={{ height: 400, width: '100%' }}>
                <DataGrid
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns ? getCustomColumns() : defaultColumns}
                    disableRowSelectionOnClick
                    processRowUpdate={handleProcessRowUpdate}
                    onCellEditStop={handleCellEditStop}
                    onColumnResize={handleColumnResize}
                    getRowClassName={(params) => (rowStyle ? JSON.stringify(rowStyle(params.row)) : '')}
                    sx={{
                        '& .MuiDataGrid-row[data-rowstyle*="backgroundColor"]': {
                            backgroundColor: (theme) => theme.palette.error.light + '20', // 20 means 20% opacity
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                            outline: 'none'
                        }
                    }}
                    checkboxSelection={!readOnly}
                    onRowSelectionModelChange={(newSelection) => {
                        setSelectedRows(newSelection as number[]);
                    }}
                />
            </div>

            <Dialog open={newColumnDialog} onClose={() => setNewColumnDialog(false)}>
                <DialogTitle>Add New Column</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Column Name"
                        fullWidth
                        variant="outlined"
                        value={newColumnName}
                        onChange={(e) => setNewColumnName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNewColumnDialog(false)}>Cancel</Button>
                    <Button onClick={handleAddColumn}>Add</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};